import { useEffect, useState } from 'react';
import { apiGet, apiSend } from '@/lib/api';
import { PageHeader, Card, Spinner, EmptyState, ErrorNote, inputClass } from '@/components/ui';

interface Saved { id: string; full_name: string; initials: string | null; relationship: string | null }

export default function Roster() {
  const [roster, setRoster] = useState<Saved[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ full_name: '', initials: '', relationship: '' });

  function load() {
    apiGet<{ roster: Saved[] }>('/api/roster')
      .then((d) => setRoster(d.roster || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function add() {
    if (!form.full_name.trim()) return;
    try {
      await apiSend('/api/roster', 'POST', {
        full_name: form.full_name.trim(),
        initials: form.initials.trim() || undefined,
        relationship: form.relationship.trim() || undefined,
      });
      setForm({ full_name: '', initials: '', relationship: '' });
      load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not save'); }
  }

  async function remove(id: string) {
    try { await apiSend(`/api/roster/${id}`, 'DELETE'); setRoster((r) => r.filter((x) => x.id !== id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not remove'); }
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
      <PageHeader title="Passenger Roster" subtitle="Saved people you fly with often — pick them fast when logging a flight." />
      {error && <div className="mb-4"><ErrorNote message={error} /></div>}

      <Card className="mb-6 p-5 sm:p-6">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-300">Add someone</h2>
        <div className="grid gap-2 sm:grid-cols-[1.6fr_0.8fr_1.4fr_auto]">
          <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} className={inputClass} />
          <input placeholder="Initials" value={form.initials} onChange={(e) => setForm((f) => ({ ...f, initials: e.target.value.toUpperCase() }))} className={inputClass} />
          <input placeholder="Relationship" value={form.relationship} onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))} className={inputClass} />
          <button onClick={add} className="btn-primary">Save</button>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-300">Saved passengers</h2>
        {loading ? (
          <Spinner />
        ) : roster.length === 0 ? (
          <EmptyState title="No saved passengers yet" hint="Add people here, or tick 'Save to roster' when logging a flight." />
        ) : (
          <ul className="divide-y divide-white/5">
            {roster.map((p) => (
              <li key={p.id} className="-mx-2 flex items-center justify-between rounded-lg px-2 py-3 transition hover:bg-white/[0.03]">
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-avio/15 text-xs font-semibold text-avio">{p.initials || p.full_name[0]}</span>
                  <div>
                    <p className="text-sm text-white">{p.full_name}</p>
                    <p className="text-xs text-slate-500">{p.relationship || '—'}</p>
                  </div>
                </div>
                <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-red-300" aria-label="Remove">✕</button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
